import { useAppTheme } from "@hooks/useAppTheme";
import type { FlashcardEntry } from "@memorang/types/src/session/new-session";
import { Box, MarkdownRenderer } from "@memorang/ui";
import { Image } from "expo-image";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	type LayoutChangeEvent,
	Pressable,
	View,
	useWindowDimensions,
} from "react-native";
import { Caption, Icon, Text } from "react-native-paper";
import Animated, {
	interpolate,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from "react-native-reanimated";
import { getPrincipleIllustration } from "../../../../../../../assets/praxis-proethica/illustrations/principles";

interface Props {
	cards: FlashcardEntry[];
	onAllViewed?: () => void;
}

// Tall enough for a comfortable front face even when the principle is short.
const MIN_CARD_HEIGHT = 240;

// Back content is "<Principle name>\n\n<description...>"; split name from body.
const splitPrinciple = (back: string) => {
	const trimmed = back.trim();
	const newlineIndex = trimmed.indexOf("\n");
	if (newlineIndex === -1) {
		return { name: trimmed, body: "" };
	}
	return {
		name: trimmed.slice(0, newlineIndex).trim(),
		body: trimmed.slice(newlineIndex).trim(),
	};
};

const PerspectiveFlipCard = ({
	card,
	index,
	sharedHeight,
	sharedHeaderHeight,
	onMeasureBack,
	onMeasureHeader,
	onViewed,
}: {
	card: FlashcardEntry;
	index: number;
	sharedHeight?: number;
	sharedHeaderHeight?: number;
	onMeasureBack: (height: number) => void;
	onMeasureHeader: (height: number) => void;
	onViewed: () => void;
}) => {
	const theme = useAppTheme();
	const flipped = useSharedValue(0);
	const [isFlipped, setIsFlipped] = useState(false);
	const [viewed, setViewed] = useState(false);
	const [backHeight, setBackHeight] = useState(0);

	const { name, body } = splitPrinciple(card.back);
	const illustration = getPrincipleIllustration(name);

	// Card sizes to the principle (the longer face) so nothing scrolls inside.
	const cardHeight = sharedHeight ?? Math.max(backHeight, MIN_CARD_HEIGHT);

	const handleBackLayout = (event: LayoutChangeEvent) => {
		const height = Math.ceil(event.nativeEvent.layout.height);
		setBackHeight((prev) => (prev === height ? prev : height));
		onMeasureBack(height);
	};

	const handleHeaderLayout = (event: LayoutChangeEvent) => {
		onMeasureHeader(Math.ceil(event.nativeEvent.layout.height));
	};

	const frontStyle = useAnimatedStyle(() => ({
		transform: [
			{ perspective: 1000 },
			{ rotateY: `${interpolate(flipped.value, [0, 1], [0, 180])}deg` },
		],
		opacity: flipped.value < 0.5 ? 1 : 0,
	}));

	const backStyle = useAnimatedStyle(() => ({
		transform: [
			{ perspective: 1000 },
			{ rotateY: `${interpolate(flipped.value, [0, 1], [180, 360])}deg` },
		],
		opacity: flipped.value >= 0.5 ? 1 : 0,
	}));

	const handleFlip = () => {
		const next = flipped.value === 0 ? 1 : 0;
		flipped.value = withTiming(next, { duration: 350 });
		setIsFlipped(next === 1);
		if (next === 1) {
			setViewed(true);
			onViewed();
		}
	};

	const baseFaceStyle = {
		position: "absolute" as const,
		top: 0,
		left: 0,
		right: 0,
		height: cardHeight,
		backfaceVisibility: "hidden" as const,
		borderRadius: theme.roundness,
		borderWidth: 1,
		borderColor: theme.colors.outlineVariant,
		backgroundColor: theme.colors.surface,
		overflow: "hidden" as const,
	};

	return (
		<View style={{ flex: 1, height: cardHeight }}>
			<Animated.View
				style={[baseFaceStyle, frontStyle]}
				accessibilityElementsHidden={isFlipped}
				importantForAccessibility={isFlipped ? "no-hide-descendants" : "auto"}
			>
				<Pressable
					onPress={handleFlip}
					style={{ flex: 1 }}
					accessibilityRole="button"
					accessibilityLabel={card.front.text}
					accessibilityHint="Double tap to reveal the principle"
				>
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
						gap={8}
						padding={16}
						backgroundColor={theme.colors.primary}
					>
						<Box flexDirection="row" alignItems="center" gap={8} flex={1}>
							<Icon
								source="account-voice"
								size={20}
								color={theme.colors.onPrimary}
							/>
							<Text
								variant="labelLarge"
								style={{ color: theme.colors.onPrimary, fontWeight: "700" }}
							>
								{`Perspective ${index + 1}`}
							</Text>
						</Box>
						<Box flexDirection="row" alignItems="center" gap={8}>
							{viewed ? null : (
								<Box
									width={8}
									height={8}
									borderRadius={4}
									backgroundColor={theme.colors.error}
								/>
							)}
							<Icon
								source="rotate-3d-variant"
								size={20}
								color={theme.colors.onPrimary}
							/>
						</Box>
					</Box>
					<Box flex={1} padding={20}>
						<MarkdownRenderer text={card.front.text} fontSize={16} />
					</Box>
					<Box
						flexDirection="row"
						alignItems="center"
						gap={6}
						paddingHorizontal={20}
						paddingVertical={14}
						borderColor={theme.colors.outlineVariant}
						style={{ borderTopWidth: 1 }}
					>
						<Icon source="gesture-tap" size={16} color={theme.colors.primary} />
						<Caption style={{ color: theme.colors.primary, fontWeight: "600" }}>
							Select to reveal the principle
						</Caption>
					</Box>
				</Pressable>
			</Animated.View>

			<Animated.View
				style={[
					baseFaceStyle,
					backStyle,
					{ pointerEvents: isFlipped ? "auto" : "none" },
				]}
				accessibilityElementsHidden={!isFlipped}
				importantForAccessibility={isFlipped ? "auto" : "no-hide-descendants"}
			>
				<View onLayout={handleBackLayout}>
					<Box
						onLayout={handleHeaderLayout}
						flexDirection="row"
						alignItems="center"
						gap={12}
						padding={16}
						borderColor={theme.colors.outlineVariant}
						style={{ borderBottomWidth: 1, minHeight: sharedHeaderHeight }}
					>
						{illustration ? (
							<Box
								width={48}
								height={48}
								borderRadius={8}
								overflow="hidden"
								backgroundColor="#FAF4ED"
								alignItems="center"
								justifyContent="center"
							>
								<Image
									source={illustration}
									contentFit="contain"
									style={{ width: 44, height: 44 }}
								/>
							</Box>
						) : (
							<Box
								width={48}
								height={48}
								borderRadius={8}
								alignItems="center"
								justifyContent="center"
								backgroundColor={theme.colors.primaryContainer}
							>
								<Icon
									source="scale-balance"
									size={24}
									color={theme.colors.primary}
								/>
							</Box>
						)}
						<Box flex={1}>
							<Caption
								style={{
									color: theme.colors.primary,
									textTransform: "uppercase",
									letterSpacing: 0.6,
									fontWeight: "700",
								}}
							>
								Principle
							</Caption>
							<Text
								variant="titleSmall"
								style={{ color: theme.colors.onSurface, fontWeight: "700" }}
							>
								{name}
							</Text>
						</Box>
						<Pressable
							onPress={handleFlip}
							accessibilityRole="button"
							accessibilityLabel="Flip card back"
							style={{
								width: 32,
								height: 32,
								alignItems: "center",
								justifyContent: "center",
								borderRadius: 16,
								backgroundColor: theme.colors.primaryContainer,
							}}
						>
							<Icon
								source="rotate-3d-variant"
								size={18}
								color={theme.colors.primary}
							/>
						</Pressable>
					</Box>
					<Box padding={20}>
						<MarkdownRenderer text={body} fontSize={15} variant="explanation" />
					</Box>
				</View>
			</Animated.View>
		</View>
	);
};

const PerspectiveFlipGrid = ({ cards, onAllViewed }: Props) => {
	const { width } = useWindowDimensions();
	const isSingleColumn = width < 760;
	const [backHeights, setBackHeights] = useState<Record<string, number>>({});
	const [headerHeights, setHeaderHeights] = useState<Record<string, number>>(
		{},
	);
	const [viewedCards, setViewedCards] = useState(() => new Set<number>());
	const hasMarkedAllViewed = useRef(false);

	const markCardViewed = useCallback((index: number) => {
		setViewedCards((current) => {
			if (current.has(index)) {
				return current;
			}

			const next = new Set(current);
			next.add(index);
			return next;
		});
	}, []);

	useEffect(() => {
		if (
			cards.length &&
			viewedCards.size === cards.length &&
			!hasMarkedAllViewed.current
		) {
			hasMarkedAllViewed.current = true;
			onAllViewed?.();
		}
	}, [cards.length, onAllViewed, viewedCards.size]);

	if (!cards.length) {
		return null;
	}

	// On desktop the row shares one height (tidy grid); on mobile each card
	// stacks and sizes to its own content.
	const measured = Object.values(backHeights);
	const sharedHeight =
		!isSingleColumn && measured.length === cards.length
			? Math.max(...measured, MIN_CARD_HEIGHT)
			: undefined;

	const measuredHeaders = Object.values(headerHeights);
	const sharedHeaderHeight =
		!isSingleColumn && measuredHeaders.length === cards.length
			? Math.max(...measuredHeaders)
			: undefined;

	return (
		<Box
			width="100%"
			flexDirection={isSingleColumn ? "column" : "row"}
			gap={16}
		>
			{cards.map((card, index) => (
				<PerspectiveFlipCard
					key={card.front.text}
					card={card}
					index={index}
					sharedHeight={sharedHeight}
					sharedHeaderHeight={sharedHeaderHeight}
					onViewed={() => markCardViewed(index)}
					onMeasureBack={(height) =>
						setBackHeights((prev) =>
							prev[card.front.text] === height
								? prev
								: { ...prev, [card.front.text]: height },
						)
					}
					onMeasureHeader={(height) =>
						setHeaderHeights((prev) =>
							prev[card.front.text] === height
								? prev
								: { ...prev, [card.front.text]: height },
						)
					}
				/>
			))}
		</Box>
	);
};

export default PerspectiveFlipGrid;
